import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Campaign as CampaignIcon,
  AutoAwesome as EnrichIcon,
  Sync as SyncIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import api from '../api';
import logger from '../utils/logger';
import SalesOutreachShell from './SalesOutreachShell';

const HubSpotRecordsTable = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Search, filter, and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [noPhoneFilter, setNoPhoneFilter] = useState(false);
  const [noEmailFilter, setNoEmailFilter] = useState(false);
  const [lifecycleStatusFilter, setLifecycleStatusFilter] = useState('');
  const [sortField, setSortField] = useState('firstname');
  const [sortOrder, setSortOrder] = useState('ASC');
  
  // Details modal state
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedRecordDetails, setSelectedRecordDetails] = useState(null);
  
  // Enrichment state
  const [enriching, setEnriching] = useState(false);
  const [enrichmentResults, setEnrichmentResults] = useState({});
  const [enrichDialogOpen, setEnrichDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [lushaResults, setLushaResults] = useState([]);
  
  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncFields, setSyncFields] = useState({});
  
  // Bulk enrichment state
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  const [bulkEnriching, setBulkEnriching] = useState(false);
  const [bulkPreviewOpen, setBulkPreviewOpen] = useState(false);
  const [bulkPreviewData, setBulkPreviewData] = useState([]);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkConfirmAgreed, setBulkConfirmAgreed] = useState(false);
  const [bulkEnrichProgress, setBulkEnrichProgress] = useState(0);
  const [bulkEnrichCurrent, setBulkEnrichCurrent] = useState(0);
  const [bulkEnrichTotal, setBulkEnrichTotal] = useState(0);
  const [bulkEnrichCreditsUsed, setBulkEnrichCreditsUsed] = useState(0);
  
  // SalesOutreach state
  const [salesOutreachOpen, setSalesOutreachOpen] = useState(false);
  const [selectedRecordForOutreach, setSelectedRecordForOutreach] = useState(null);
  
  useEffect(() => {
    fetchRecords();
  }, [page, rowsPerPage, searchQuery, noPhoneFilter, noEmailFilter, lifecycleStatusFilter, sortField, sortOrder]);
  
  const fetchRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filters = {};
      if (noPhoneFilter) {
        filters.no_phone = true;
      }
      if (noEmailFilter) {
        filters.no_email = true;
      }
      if (lifecycleStatusFilter) {
        filters.lifecycle_status = lifecycleStatusFilter;
      }
      
      const result = await api.hubspotGetRecords(
        rowsPerPage,
        page * rowsPerPage,
        filters,
        searchQuery,
        sortField,
        sortOrder
      );
      
      if (result.success) {
        setRecords(result.records || []);
        const total = result.total_size || 0;
        setTotalRecords(total);
        logger.log(`Fetched ${result.records?.length || 0} HubSpot contacts, total: ${total}, page: ${page}, offset: ${page * rowsPerPage}`);
      } else {
        setError(result.error || 'Failed to fetch records');
      }
    } catch (err) {
      logger.error('Error fetching HubSpot contacts:', err);
      setError(err.response?.data?.error || err.message || 'Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page when searching
  };
  
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle sort order if clicking same field
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      // Set new sort field, default to ASC
      setSortField(field);
      setSortOrder('ASC');
    }
    setPage(0); // Reset to first page when sorting
  };
  
  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortOrder === 'ASC' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />;
  };
  
  const handleRowClick = (record) => {
    setSelectedRecordDetails(record);
    setDetailsDialogOpen(true);
  };
  
  const handleEnrich = async (record) => {
    try {
      setEnriching(true);
      setError(null);
      setSelectedRecord(record);
      
      const result = await api.hubspotEnrich([record.id]);
      
      if (result.success && result.results && result.results.length > 0) {
        const enrichmentResult = result.results[0];
        const lushaResults = enrichmentResult.lusha_results || [];
        
        // Check if we have meaningful contact data (not just empty objects)
        const hasMeaningfulData = lushaResults.some(contact => 
          contact.email || 
          contact.phone || 
          (contact.firstName && contact.lastName) ||
          contact.linkedinUrl
        );
        
        if (hasMeaningfulData) {
          setLushaResults(lushaResults);
          setEnrichmentResults({
            ...enrichmentResults,
            [record.id]: enrichmentResult
          });
          setEnrichDialogOpen(true);
        } else {
          setError(enrichmentResult.error || 'No contact information found in enrichment results');
        }
      } else {
        setError('No enrichment results found');
      }
    } catch (err) {
      logger.error('Error enriching HubSpot contact:', err);
      setError(err.response?.data?.error || err.message || 'Failed to enrich contact');
    } finally {
      setEnriching(false);
    }
  };
  
  const handleSync = async () => {
    if (!selectedRecord || Object.keys(syncFields).length === 0) {
      setError('Please select fields to sync');
      return;
    }
    
    try {
      setSyncing(true);
      setError(null);
      
      const result = await api.hubspotSync(selectedRecord.id, syncFields);
      
      if (result.success) {
        setSyncDialogOpen(false);
        setSyncFields({});
        await fetchRecords(); // Refresh records
        setError(null);
      } else {
        setError(result.error || 'Failed to sync to HubSpot');
      }
    } catch (err) {
      logger.error('Error syncing to HubSpot:', err);
      setError(err.response?.data?.error || err.message || 'Failed to sync');
    } finally {
      setSyncing(false);
    }
  };
  
  const handleSelectAll = () => {
    if (selectedRecords.size === records.length) {
      setSelectedRecords(new Set());
    } else {
      setSelectedRecords(new Set(records.map(r => r.id)));
    }
  };
  
  const handleSelectRecord = (recordId) => {
    const newSelected = new Set(selectedRecords);
    if (newSelected.has(recordId)) {
      newSelected.delete(recordId);
    } else {
      newSelected.add(recordId);
    }
    setSelectedRecords(newSelected);
  };
  
  const handleBulkEnrichClick = () => {
    if (selectedRecords.size === 0) {
      setError('Please select at least one contact to enrich');
      return;
    }
    
    // Show confirmation dialog first
    setBulkConfirmAgreed(false);
    setBulkConfirmOpen(true);
  };
  
  const handleBulkEnrichConfirm = async () => {
    if (!bulkConfirmAgreed) {
      setError('Please acknowledge the credit usage warning before proceeding');
      return;
    }
    
    setBulkConfirmOpen(false);
    await performBulkEnrich();
  };
  
  const performBulkEnrich = async () => {
    try {
      setBulkEnriching(true);
      setBulkEnrichProgress(0);
      setBulkEnrichCurrent(0);
      setBulkEnrichCreditsUsed(0);
      setError(null);
      
      const contactIds = Array.from(selectedRecords);
      const totalContacts = contactIds.length;
      setBulkEnrichTotal(totalContacts);
      
      // Process contacts one by one for real-time progress feedback
      const enrichmentResults = [];
      let totalCreditsUsed = 0;
      
      for (let i = 0; i < contactIds.length; i++) {
        const contactId = contactIds[i];
        const currentNumber = i + 1;
        
        try {
          // Enrich one contact at a time
          const result = await api.hubspotEnrich([contactId]);
          
          if (result.success && result.results && result.results.length > 0) {
            const enrichmentResult = result.results[0];
            enrichmentResults.push(enrichmentResult);
            
            // Track credits used
            const creditsUsed = enrichmentResult.credits_used || 0;
            totalCreditsUsed += creditsUsed;
            setBulkEnrichCreditsUsed(totalCreditsUsed);
          } else {
            enrichmentResults.push({
              hubspot_contact_id: contactId,
              enrichment_success: false,
              error: result.error || 'Enrichment failed',
              lusha_results: []
            });
          }
          
          // Update progress after each contact
          const progressPercent = (currentNumber / totalContacts) * 100;
          setBulkEnrichCurrent(currentNumber);
          setBulkEnrichProgress(progressPercent);
        } catch (err) {
          logger.error(`Error enriching contact ${contactId}:`, err);
          enrichmentResults.push({
            hubspot_contact_id: contactId,
            enrichment_success: false,
            error: err.response?.data?.error || err.message || 'Enrichment error',
            lusha_results: []
          });
          
          // Still update progress even on error
          const progressPercent = (currentNumber / totalContacts) * 100;
          setBulkEnrichCurrent(currentNumber);
          setBulkEnrichProgress(progressPercent);
        }
      }
      
      // Process results to create preview data
      const result = {
        success: true,
        results: enrichmentResults
      };
      
      if (result.success && result.results) {
        // Process results to create preview data
        const previewData = result.results.map((enrichmentResult) => {
          const record = records.find(r => r.id === enrichmentResult.hubspot_contact_id);
          const lushaContact = enrichmentResult.lusha_results?.[0];
          
          if (!lushaContact || !enrichmentResult.enrichment_success) {
            return {
              contactId: enrichmentResult.hubspot_contact_id,
              contactName: record ? `${record.firstname || ''} ${record.lastname || ''}`.trim() || 'Unknown' : 'Unknown',
              error: enrichmentResult.error || 'No data found',
              hasData: false,
              ignored: false // Track if user wants to ignore this record
            };
          }
          
          // Compare existing vs new data
          const changes = {
            added: [],
            changed: []
          };
          
          // Email comparison
          if (lushaContact.email) {
            const existingEmail = record?.email || '';
            if (!existingEmail) {
              changes.added.push({ field: 'email', value: lushaContact.email });
            } else if (existingEmail.toLowerCase() !== lushaContact.email.toLowerCase()) {
              changes.changed.push({ 
                field: 'email', 
                oldValue: existingEmail, 
                newValue: lushaContact.email 
              });
            }
          }
          
          // Phone comparison
          if (lushaContact.phone) {
            const existingPhone = record?.phone || '';
            if (!existingPhone) {
              changes.added.push({ field: 'phone', value: lushaContact.phone });
            } else if (existingPhone !== lushaContact.phone) {
              changes.changed.push({ 
                field: 'phone', 
                oldValue: existingPhone, 
                newValue: lushaContact.phone 
              });
            }
          }
          
          // Company name comparison - only sync if missing
          if (lushaContact.companyName) {
            const existingCompany = record?.company || '';
            if (!existingCompany || !existingCompany.trim()) {
              changes.added.push({ field: 'company', value: lushaContact.companyName });
            }
          }
          
          // Job title comparison - only sync if missing
          const jobTitleValue = typeof lushaContact.jobTitle === 'object' && lushaContact.jobTitle.title 
            ? lushaContact.jobTitle.title 
            : lushaContact.jobTitle;
          if (jobTitleValue) {
            const existingJobTitle = record?.jobtitle || '';
            if (!existingJobTitle || !existingJobTitle.trim()) {
              changes.added.push({ field: 'jobTitle', value: jobTitleValue }); // Use camelCase for backend mapping
            }
          }
          
          return {
            contactId: enrichmentResult.hubspot_contact_id,
            contactName: record ? `${record.firstname || ''} ${record.lastname || ''}`.trim() || 'Unknown' : 'Unknown',
            lushaContact,
            changes,
            hasData: true,
            ignored: false // Track if user wants to ignore this record
          };
        });
        
        setBulkPreviewData(previewData);
        setBulkPreviewOpen(true);
      } else {
        setError(result.error || 'Failed to enrich contacts');
      }
    } catch (err) {
      logger.error('Error bulk enriching HubSpot contacts:', err);
      setError(err.response?.data?.error || err.message || 'Failed to enrich contacts');
    } finally {
      setBulkEnriching(false);
    }
  };
  
  const handleBulkSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      
      const contactsToSync = bulkPreviewData.filter(
        item => !item.ignored && item.hasData && (item.changes.added.length > 0 || item.changes.changed.length > 0)
      );
      
      if (contactsToSync.length === 0) {
        setError('No contacts with changes to sync');
        return;
      }
      
      // Sync each contact
      const syncResults = [];
      for (const item of contactsToSync) {
        const fieldsToSync = {};
        
        // Add new fields
        item.changes.added.forEach(change => {
          fieldsToSync[change.field] = change.value;
        });
        
        // Update changed fields
        item.changes.changed.forEach(change => {
          fieldsToSync[change.field] = change.newValue;
        });
        
        try {
          const result = await api.hubspotSync(item.contactId, fieldsToSync);
          syncResults.push({ contactId: item.contactId, success: result.success, error: result.error });
        } catch (err) {
          syncResults.push({ contactId: item.contactId, success: false, error: err.message });
        }
      }
      
      const successCount = syncResults.filter(r => r.success).length;
      if (successCount > 0) {
        setBulkPreviewOpen(false);
        setSelectedRecords(new Set());
        await fetchRecords(); // Refresh records
        setSuccess(`Successfully synced ${successCount} of ${contactsToSync.length} contacts`);
      } else {
        setError('Failed to sync contacts');
      }
    } catch (err) {
      logger.error('Error bulk syncing to HubSpot:', err);
      setError(err.response?.data?.error || err.message || 'Failed to sync');
    } finally {
      setSyncing(false);
    }
  };
  
  const extractDomain = (website, email) => {
    // Try website first
    if (website && website.trim() && website !== 'N/A') {
      try {
        // Remove protocol, www, and path
        let domain = website.replace(/^https?:\/\//, '').replace(/^www\./, '');
        domain = domain.split('/')[0].split('?')[0];
        domain = domain.replace(/\.$/, '');
        
        // Make sure it's a valid domain (contains at least one dot)
        if (domain.includes('.')) {
          return domain.toLowerCase();
        }
      } catch (e) {
        logger.error('Error extracting domain from website:', e);
      }
    }
    
    // Fallback to email domain
    if (email && email.includes('@')) {
      const domain = email.split('@')[1];
      if (domain && domain.includes('.')) {
        return domain.toLowerCase();
      }
    }
    
    return null;
  };
  
  const formatUrl = (url) => {
    if (!url) return url;
    url = url.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    try {
      const urlObj = new URL(url);
      return urlObj.href;
    } catch (e) {
      logger.error('Invalid URL format:', url);
      return url;
    }
  };

  const extractDomainFromUrl = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.toLowerCase();
    } catch (e) {
      logger.error('Failed to extract domain from URL:', url);
      return url.replace(/^https?:\/\//, '').split('/')[0].toLowerCase();
    }
  };

  const extractCompanyNameFromUrl = (url) => {
    try {
      const domain = extractDomainFromUrl(url);
      const parts = domain.split('.');
      if (parts.length >= 2) {
        return parts[parts.length - 2].charAt(0).toUpperCase() + parts[parts.length - 2].slice(1);
      }
      return domain;
    } catch (e) {
      logger.error('Failed to extract company name from URL:', url);
      return 'Unknown Company';
    }
  };

  const handleOpenSalesOutreach = async (record) => {
    // Extract domain from website or email
    const domain = extractDomain(record.website, record.email);
    
    if (!domain) {
      setError('No website or email domain found for this contact');
      return;
    }
    
    // Get website URL or construct from domain
    let websiteUrl = record.website || `https://${domain}`;
    
    // Format URL properly
    const cleanUrl = formatUrl(websiteUrl);
    const companyName = record.company || extractCompanyNameFromUrl(cleanUrl);
    
    try {
      // Check if a database record exists for this URL (like domain search does)
      logger.log(`Checking if URL exists in records: ${cleanUrl} (Domain: ${domain})`);
      const urlCheckResult = await api.checkUrlInRecords(cleanUrl);
      
      let dbRecord;
      
      if (urlCheckResult.success && urlCheckResult.found && urlCheckResult.records.length > 0) {
        logger.log(`Found ${urlCheckResult.count} existing records for URL: ${cleanUrl}`);
        dbRecord = urlCheckResult.records[0];
      } else {
        logger.log(`No existing records found for URL: ${cleanUrl}, creating a new record`);
        
        // Create a database record like domain search does
        dbRecord = {
          Domain: domain,
          Instellingsnaam: companyName,
          Telefoonnummer: '-',
          URL: cleanUrl,
          'Vestigingsadres Plaats': '-',
          Website: cleanUrl,
          record_id: crypto.randomUUID()
        };
        
        try {
          const addResult = await api.addRecord(dbRecord);
          if (addResult.success) {
            dbRecord.record_id = addResult.record_id;
            logger.log(`Created new record with ID: ${dbRecord.record_id}`);
          }
        } catch (addErr) {
          logger.error('Error adding record:', addErr);
        }
      }
      
      // Map HubSpot contact to format expected by SalesOutreach, merging with database record
      // Only include relevant fields from HubSpot record (email, name, phone, etc.)
      const contactInfo = {
        ...dbRecord,
        email: record.email || '',
        firstName: record.firstname || '',
        lastName: record.lastname || '',
        fullName: `${record.firstname || ''} ${record.lastname || ''}`.trim() || 'Unknown',
        phone: record.phone || '',
        company: record.company || '',
        companyName: companyName,
        jobTitle: record.jobtitle || '',
        website: cleanUrl,
        Website: cleanUrl,
        URL: cleanUrl,
        Domain: domain
      };
      
      setSelectedRecordForOutreach({
        contactInfo,
        dbRecord
      });
      setSalesOutreachOpen(true);
    } catch (err) {
      logger.error('Error preparing SalesOutreach:', err);
      setError('Failed to prepare outreach data. Please try again.');
    }
  };
  
  const handleCloseSalesOutreach = () => {
    setSalesOutreachOpen(false);
    setSelectedRecordForOutreach(null);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(parseInt(dateString));
      return date.toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };
  
  const getLifecycleStageColor = (stage) => {
    if (!stage) return 'default';
    const stageLower = stage.toLowerCase();
    // Map HubSpot lifecycle stages to colors
    if (stageLower.includes('lead') || stageLower.includes('subscriber')) {
      return 'info';
    } else if (stageLower.includes('qualified') || stageLower.includes('marketing')) {
      return 'primary';
    } else if (stageLower.includes('customer') || stageLower.includes('evangelist')) {
      return 'success';
    } else if (stageLower.includes('opportunity') || stageLower.includes('sales')) {
      return 'warning';
    }
    return 'default';
  };
  
  return (
    <Box>
      {/* Search and Filters */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search contacts..."
          value={searchQuery}
          onChange={handleSearchChange}
          size="small"
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        
        <FormControlLabel
          control={
            <Checkbox
              checked={noEmailFilter}
              onChange={(e) => {
                setNoEmailFilter(e.target.checked);
                setPage(0);
              }}
            />
          }
          label="No Email"
        />
        
        <FormControlLabel
          control={
            <Checkbox
              checked={noPhoneFilter}
              onChange={(e) => {
                setNoPhoneFilter(e.target.checked);
                setPage(0);
              }}
            />
          }
          label="No Phone"
        />
        
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Lifecycle Status</InputLabel>
          <Select
            value={lifecycleStatusFilter}
            label="Lifecycle Status"
            onChange={(e) => {
              setLifecycleStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="subscriber">Subscriber</MenuItem>
            <MenuItem value="lead">Lead</MenuItem>
            <MenuItem value="marketingqualifiedlead">Marketing Qualified Lead</MenuItem>
            <MenuItem value="salesqualifiedlead">Sales Qualified Lead</MenuItem>
            <MenuItem value="opportunity">Opportunity</MenuItem>
            <MenuItem value="customer">Customer</MenuItem>
            <MenuItem value="evangelist">Evangelist</MenuItem>
          </Select>
        </FormControl>
        
        {selectedRecords.size > 0 && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<EnrichIcon />}
            onClick={handleBulkEnrichClick}
            disabled={bulkEnriching || loading}
            size="small"
          >
            Bulk Enrich ({selectedRecords.size})
          </Button>
        )}
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchRecords}
          variant="outlined"
          size="small"
        >
          Refresh
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedRecords.size > 0 && selectedRecords.size < records.length}
                  checked={records.length > 0 && selectedRecords.size === records.length}
                  onChange={handleSelectAll}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => handleSort('firstname')}>
                  Name
                  {getSortIcon('firstname')}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => handleSort('email')}>
                  Email
                  {getSortIcon('email')}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => handleSort('company')}>
                  Company
                  {getSortIcon('company')}
                </Box>
              </TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Job Title</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => handleSort('lifecyclestage')}>
                  Lifecycle Status
                  {getSortIcon('lifecyclestage')}
                </Box>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No contacts found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              records.map((record) => (
                <TableRow 
                  key={record.id} 
                  hover
                  onClick={() => handleRowClick(record)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedRecords.has(record.id)}
                      onChange={() => handleSelectRecord(record.id)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {`${record.firstname || ''} ${record.lastname || ''}`.trim() || 'N/A'}
                  </TableCell>
                  <TableCell>{record.email || 'N/A'}</TableCell>
                  <TableCell>{record.company || 'N/A'}</TableCell>
                  <TableCell>{record.phone || 'N/A'}</TableCell>
                  <TableCell>{record.jobtitle || 'N/A'}</TableCell>
                  <TableCell>
                    {record.lifecyclestage ? (
                      <Chip 
                        label={record.lifecyclestage} 
                        size="small" 
                        color={getLifecycleStageColor(record.lifecyclestage)}
                        variant="outlined"
                      />
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      {record.email && (
                        <Tooltip title="Sales Outreach">
                          <IconButton size="small" onClick={() => handleOpenSalesOutreach(record)} color="default">
                            <CampaignIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Enrich Contact">
                        <IconButton
                          size="small"
                          onClick={() => handleEnrich(record)}
                          disabled={enriching}
                        >
                          <EnrichIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
      
      {/* Details Dialog */}
      {selectedRecordDetails && (
        <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Contact Details
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">First Name</Typography>
                <Typography variant="body1">{selectedRecordDetails.firstname || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Last Name</Typography>
                <Typography variant="body1">{selectedRecordDetails.lastname || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{selectedRecordDetails.email || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Phone</Typography>
                <Typography variant="body1">{selectedRecordDetails.phone || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Company</Typography>
                <Typography variant="body1">{selectedRecordDetails.company || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Job Title</Typography>
                <Typography variant="body1">{selectedRecordDetails.jobtitle || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Website</Typography>
                <Typography variant="body1">{selectedRecordDetails.website || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Contact ID</Typography>
                <Typography variant="body1">{selectedRecordDetails.id || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Last Modified</Typography>
                <Typography variant="body1">{formatDate(selectedRecordDetails.hs_lastmodifieddate)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Created</Typography>
                <Typography variant="body1">{formatDate(selectedRecordDetails.createdate)}</Typography>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
              {selectedRecordDetails && (
                <Button
                  variant="contained"
                  startIcon={<EnrichIcon />}
                  onClick={() => {
                    setDetailsDialogOpen(false);
                    handleEnrich(selectedRecordDetails);
                  }}
                  disabled={enriching}
                >
                  Enrich
                </Button>
              )}
            </Box>
          </Box>
        </Dialog>
      )}
      
      {/* Enrichment Dialog */}
      <Dialog
        open={enrichDialogOpen}
        onClose={() => setEnrichDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Enrichment Results - {selectedRecord && `${selectedRecord.firstname || ''} ${selectedRecord.lastname || ''}`.trim()}
        </DialogTitle>
        <DialogContent>
          {lushaResults.length > 0 ? (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Found {lushaResults.length} contact(s) from Lusha:
              </Typography>
              {lushaResults.map((contact, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h6">
                        {contact.firstName} {contact.lastName}
                      </Typography>
                      {contact.jobTitle && (
                        <Typography variant="body2" color="text.secondary">
                          {typeof contact.jobTitle === 'object' && contact.jobTitle.title 
                            ? contact.jobTitle.title 
                            : contact.jobTitle}
                        </Typography>
                      )}
                    </Grid>
                    {contact.email && (
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          <strong>Email:</strong> {contact.email}
                        </Typography>
                      </Grid>
                    )}
                    {contact.phone && (
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          <strong>Phone:</strong> {contact.phone}
                        </Typography>
                      </Grid>
                    )}
                    {contact.linkedinUrl && (
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          <strong>LinkedIn:</strong>{' '}
                          <a href={contact.linkedinUrl} target="_blank" rel="noopener noreferrer">
                            {contact.linkedinUrl}
                          </a>
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              ))}
              <Button
                variant="contained"
                startIcon={<SyncIcon />}
                onClick={async () => {
                  // Sync directly without confirmation dialog
                  if (lushaResults.length > 0 && selectedRecord) {
                    const firstResult = lushaResults[0];
                    const fieldsToSync = {};
                    
                    // Sync email if missing or different
                    if (firstResult.email) {
                      if (!selectedRecord.email || selectedRecord.email.toLowerCase() !== firstResult.email.toLowerCase()) {
                        fieldsToSync.email = firstResult.email;
                      }
                    }
                    
                    // Sync phone if missing or different
                    if (firstResult.phone) {
                      if (!selectedRecord.phone || selectedRecord.phone !== firstResult.phone) {
                        fieldsToSync.phone = firstResult.phone;
                      }
                    }
                    
                    // Sync company name if missing
                    const jobTitleValue = typeof firstResult.jobTitle === 'object' && firstResult.jobTitle.title 
                      ? firstResult.jobTitle.title 
                      : firstResult.jobTitle;
                    if (firstResult.companyName && (!selectedRecord.company || !selectedRecord.company.trim())) {
                      fieldsToSync.company = firstResult.companyName;
                    }
                    
                    // Sync job title if missing
                    if (jobTitleValue && (!selectedRecord.jobtitle || !selectedRecord.jobtitle.trim())) {
                      fieldsToSync.jobTitle = jobTitleValue;
                    }
                    
                    if (Object.keys(fieldsToSync).length > 0) {
                      try {
                        setSyncing(true);
                        setError(null);
                        
                        const result = await api.hubspotSync(selectedRecord.id, fieldsToSync);
                        
                        if (result.success) {
                          setEnrichDialogOpen(false);
                          await fetchRecords(); // Refresh records
                          setError(null);
                        } else {
                          setError(result.error || 'Failed to sync to HubSpot');
                        }
                      } catch (err) {
                        logger.error('Error syncing to HubSpot:', err);
                        setError(err.response?.data?.error || err.message || 'Failed to sync');
                      } finally {
                        setSyncing(false);
                      }
                    } else {
                      setError('No fields available to sync (all fields already populated)');
                    }
                  }
                }}
                disabled={syncing}
                sx={{ mt: 2 }}
              >
                {syncing ? 'Syncing...' : 'Sync to HubSpot'}
              </Button>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No enrichment results found for this contact.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrichDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Sync Dialog */}
      <Dialog
        open={syncDialogOpen}
        onClose={() => setSyncDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Sync Enriched Data to HubSpot</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which fields to update in HubSpot:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email"
              value={syncFields.email || ''}
              onChange={(e) => setSyncFields({ ...syncFields, email: e.target.value })}
              fullWidth
            />
            <TextField
              label="Phone"
              value={syncFields.phone || ''}
              onChange={(e) => setSyncFields({ ...syncFields, phone: e.target.value })}
              fullWidth
            />
            <TextField
              label="Company Name"
              value={syncFields.company || ''}
              onChange={(e) => setSyncFields({ ...syncFields, company: e.target.value })}
              fullWidth
            />
            <TextField
              label="Job Title"
              value={syncFields.jobTitle || ''}
              onChange={(e) => setSyncFields({ ...syncFields, jobTitle: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSync}
            variant="contained"
            disabled={syncing || Object.keys(syncFields).length === 0}
          >
            {syncing ? <CircularProgress size={24} /> : 'Sync'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Bulk Enrichment Preview Dialog */}
      <Dialog
        open={bulkPreviewOpen}
        onClose={() => setBulkPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box>
            <Typography variant="h6">
              Bulk Enrichment Preview - {bulkPreviewData.length} Contact(s)
            </Typography>
            {bulkPreviewData.filter(item => item.ignored).length > 0 && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                {bulkPreviewData.filter(item => item.ignored).length} contact(s) marked to ignore
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Review the changes that will be made to HubSpot contacts. Fields marked with <strong>+</strong> will be added, fields marked with <strong>→</strong> will be updated. Use the <strong>Ignore</strong> checkbox to exclude records from syncing.
            </Typography>
            
            {bulkPreviewData.map((item, index) => (
              <Paper 
                key={index} 
                sx={{ 
                  p: 2, 
                  mb: 2,
                  opacity: item.ignored ? 0.6 : 1,
                  border: item.ignored ? '2px dashed' : '1px solid',
                  borderColor: item.ignored ? 'warning.main' : 'divider'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {item.contactName}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={item.ignored || false}
                        onChange={(e) => {
                          const updatedData = [...bulkPreviewData];
                          updatedData[index].ignored = e.target.checked;
                          setBulkPreviewData(updatedData);
                        }}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" color={item.ignored ? 'warning.main' : 'text.secondary'}>
                        {item.ignored ? 'Ignored' : 'Ignore'}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                </Box>
                {item.ignored && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This record will be skipped during sync.
                  </Alert>
                )}
                
                {!item.hasData ? (
                  <Alert severity="warning">
                    {item.error || 'No enrichment data available'}
                  </Alert>
                ) : (
                  <Box>
                    {item.changes.added.length === 0 && item.changes.changed.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No changes to apply (all fields already match)
                      </Typography>
                    ) : (
                      <Grid container spacing={2}>
                        {item.changes.added.length > 0 && (
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" color="success.main" gutterBottom>
                              Fields to Add:
                            </Typography>
                            {item.changes.added.map((change, idx) => (
                              <Box key={idx} sx={{ mb: 1, pl: 2 }}>
                                <Typography variant="body2">
                                  <strong>+ {change.field}:</strong> {typeof change.value === 'object' ? JSON.stringify(change.value) : change.value}
                                </Typography>
                              </Box>
                            ))}
                          </Grid>
                        )}
                        
                        {item.changes.changed.length > 0 && (
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" color="warning.main" gutterBottom>
                              Fields to Update:
                            </Typography>
                            {item.changes.changed.map((change, idx) => (
                              <Box key={idx} sx={{ mb: 1, pl: 2 }}>
                                <Typography variant="body2">
                                  <strong>→ {change.field}:</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                                  Current: {typeof change.oldValue === 'object' ? JSON.stringify(change.oldValue) : (change.oldValue || 'N/A')}
                                </Typography>
                                <Typography variant="body2" color="primary.main" sx={{ pl: 2 }}>
                                  New: {typeof change.newValue === 'object' ? JSON.stringify(change.newValue) : change.newValue}
                                </Typography>
                              </Box>
                            ))}
                          </Grid>
                        )}
                      </Grid>
                    )}
                  </Box>
                )}
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkPreviewOpen(false)} disabled={syncing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            onClick={handleBulkSync}
            disabled={syncing || bulkPreviewData.filter(item => !item.ignored && item.hasData && (item.changes.added.length > 0 || item.changes.changed.length > 0)).length === 0}
          >
            {syncing ? 'Syncing...' : 'Sync to HubSpot'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Bulk Enrichment Confirmation Dialog */}
      <Dialog
        open={bulkConfirmOpen}
        onClose={() => {
          if (!bulkEnriching) {
            setBulkConfirmOpen(false);
            setBulkConfirmAgreed(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ color: 'warning.main' }} />
            <Typography variant="h6">Bulk Enrichment Warning</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Credit Usage Warning
            </Typography>
            <Typography variant="body2">
              You are about to enrich <strong>{selectedRecords.size} contact(s)</strong>. Bulk enrichment actions can generate high volumes of credit usage and may:
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              <li><Typography variant="body2">Exceed limits of fair use</Typography></li>
              <li><Typography variant="body2">Result in additional costs to your subscription</Typography></li>
              <li><Typography variant="body2">Consume significant credits from your account</Typography></li>
            </Box>
            <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
              Please ensure you understand the potential costs before proceeding.
            </Typography>
          </Alert>
          <FormControlLabel
            control={
              <Checkbox
                checked={bulkConfirmAgreed}
                onChange={(e) => setBulkConfirmAgreed(e.target.checked)}
                disabled={bulkEnriching}
              />
            }
            label="I understand the potential credit usage and costs, and agree to proceed with bulk enrichment"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setBulkConfirmOpen(false);
              setBulkConfirmAgreed(false);
            }}
            disabled={bulkEnriching}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkEnrichConfirm}
            variant="contained"
            color="warning"
            disabled={bulkEnriching || !bulkConfirmAgreed}
          >
            Proceed with Bulk Enrichment
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Bulk Enrichment Progress Dialog */}
      <Dialog
        open={bulkEnriching}
        onClose={() => {}} // Prevent closing during enrichment
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Enrichment in Progress</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Enriching {bulkEnrichTotal || selectedRecords.size} contact(s)... Please wait.
            </Typography>
            <Box sx={{ mt: 3, mb: 2 }}>
              <LinearProgress 
                variant="determinate" 
                value={bulkEnrichProgress} 
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {bulkEnrichCurrent > 0 ? `${bulkEnrichCurrent} / ${bulkEnrichTotal}` : 'Starting...'} ({Math.round(bulkEnrichProgress)}%)
                </Typography>
                <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'bold' }}>
                  Credits Used: {bulkEnrichCreditsUsed}
                </Typography>
              </Box>
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              This may take a few moments depending on the number of contacts selected.
            </Alert>
          </Box>
        </DialogContent>
      </Dialog>
      
      {/* Sales Outreach */}
      {selectedRecordForOutreach && selectedRecordForOutreach.dbRecord && (
        <SalesOutreachShell
          open={salesOutreachOpen}
          onClose={handleCloseSalesOutreach}
          recordId={selectedRecordForOutreach.dbRecord.record_id}
          companyName={selectedRecordForOutreach.contactInfo.Instellingsnaam || selectedRecordForOutreach.contactInfo.companyName || selectedRecordForOutreach.contactInfo.company || 'Unknown Company'}
          contactInfo={selectedRecordForOutreach.contactInfo}
          openWithUrlSearch={true}
        />
      )}
    </Box>
  );
};

export default HubSpotRecordsTable;

